import { styled } from "@mui/material/styles";
import {
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    DialogProps,
    Grid,
    InternalStandardProps,
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AppButton } from "./AppButton";
import { AppTypography } from "./AppTypography";
import { AppCircularProgress } from "./Progress/AppCircularProgress";

const StyledDialogWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: `${theme.palette.background.default}`,
}));

const StyledTypographyGrey600 = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

interface StyledMainTextProps {
    isSmallTitle?: boolean;
    isTextSize?: boolean;
    preserveLineBreaks?: boolean;
}

const StyledMainText = styled(AppTypography, {
    shouldForwardProp: (prop) =>
        prop !== "isSmallTitle" &&
        prop !== "isTextSize" &&
        prop !== "preserveLineBreaks",
})<StyledMainTextProps>(
    ({ theme, isSmallTitle, isTextSize, preserveLineBreaks }) => ({
        color: theme.palette.grey[600],
        ...(isSmallTitle && {
            fontSize: "1rem",
        }),
        ...(isTextSize && {
            fontSize: "1.1rem",
        }),
        ...(preserveLineBreaks && {
            whiteSpace: "pre-line",
        }),
    }),
);

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    "& .MuiFormControlLabel-label": {
        fontSize: "14px",
        color: theme.palette.common.black,
        whiteSpace: "nowrap",
    },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
    padding: 0,
    color: `${theme.palette.grey[700]}`,
    "&.Mui-checked": {
        color: `${theme.palette.primary.main} !important`,
    },
}));

const StyledMt1Grid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const StyledCloseIconBtn = styled(IconButton)(({ theme }) => ({
    padding: 0,
    marginRight: "10px",
    marginTop: "10px",
    color: theme.palette.grey[600],
}));

const StyledCloseIconBtnContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "end",
    backgroundColor: theme.palette.background.default,
    border: "none",
}));

interface StyledDialogContentProps {
    showCloseIcon?: boolean;
}

const StyledDialogContent = styled(DialogContent, {
    shouldForwardProp: (prop) => prop !== "showCloseIcon",
})<StyledDialogContentProps>(({ theme, showCloseIcon }) => ({
    backgroundColor: `${theme.palette.background.default}`,
    ...(showCloseIcon
        ? {
              paddingTop: 0,
          }
        : {
              paddingTop: "20px",
          }),
}));

export interface AppConfimationDialogProps
    extends InternalStandardProps<DialogProps> {
    onClose: () => void;
    onConfirm?: () => void;
    confirmText?: string;
    popupMainText: string;
    showCancelButton?: boolean;
    cancelButtonText?: string;
    smallBodyText?: string;
    btnConfirmLoader?: boolean;
    showConfirmFirst?: boolean;
    showTitle?: boolean;
    titleText?: string;
    titleSize?: "small";
    type?: "download-warning";
    doNotShow?: boolean;
    setDoNotShow?: (val: boolean) => void;
    showButtonBackgroundColor?: boolean;
    showCloseIcon?: boolean;
    preserveLineBreaks?: boolean;
}

export const AppConfimationDialog = (props: AppConfimationDialogProps) => {
    const {
        className,
        onClose,
        onConfirm,
        confirmText,
        showCancelButton = true,
        cancelButtonText,
        popupMainText,
        btnConfirmLoader,
        showConfirmFirst,
        showTitle,
        titleText,
        titleSize,
        type,
        smallBodyText,
        doNotShow,
        setDoNotShow,
        showButtonBackgroundColor,
        showCloseIcon,
        preserveLineBreaks,
        ...rest
    } = props;

    return (
        <Dialog
            role="dialog"
            aria-modal="true"
            maxWidth={"sm"}
            fullWidth
            className={className}
            {...rest}
        >
            {showCloseIcon && (
                <StyledCloseIconBtnContainer>
                    <StyledCloseIconBtn
                        aria-label="close"
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </StyledCloseIconBtn>
                </StyledCloseIconBtnContainer>
            )}
            <StyledDialogWrapper className={className}>
                {showTitle && (
                    <DialogTitle>
                        <Grid container>
                            <Grid item xs={12}>
                                <AppTypography
                                    variant="h6"
                                    aria-label={titleText}
                                >
                                    {titleText}
                                </AppTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                )}

                <StyledDialogContent showCloseIcon={showCloseIcon}>
                    <Grid container>
                        <Grid item xs={12}>
                            <StyledMainText
                                variant={!showCancelButton ? "body1" : "h6"}
                                isSmallTitle={titleSize === "small"}
                                isTextSize={!showCancelButton}
                                preserveLineBreaks={preserveLineBreaks}
                            >
                                {popupMainText}
                            </StyledMainText>
                        </Grid>
                        {smallBodyText && (
                            <StyledMt1Grid item xs={12}>
                                <StyledTypographyGrey600 variant="body1">
                                    {smallBodyText}
                                </StyledTypographyGrey600>
                            </StyledMt1Grid>
                        )}
                    </Grid>
                </StyledDialogContent>
                {type === "download-warning" && (
                    <Box>
                        <StyledFormControlLabel
                            label="Don't show again"
                            control={
                                <StyledCheckbox
                                    style={{
                                        marginLeft: "32px",
                                        marginRight: "5px",
                                    }}
                                    checked={doNotShow}
                                    onChange={(e) =>
                                        setDoNotShow(e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                        />
                    </Box>
                )}
                <DialogActions
                    style={{
                        display: "flex",
                    }}
                >
                    {showConfirmFirst && (
                        <>
                            {showCancelButton && (
                                <AppButton
                                    buttonVariant={
                                        showButtonBackgroundColor
                                            ? "primary-filled"
                                            : "grey-text"
                                    }
                                    role="button"
                                    onClick={onClose}
                                >
                                    {cancelButtonText}
                                </AppButton>
                            )}
                            {confirmText &&
                                (!btnConfirmLoader ? (
                                    <AppButton
                                        role="button"
                                        buttonVariant={
                                            showButtonBackgroundColor
                                                ? "primary-filled"
                                                : "grey-text"
                                        }
                                        onClick={onConfirm}
                                    >
                                        {confirmText}
                                    </AppButton>
                                ) : (
                                    <AppCircularProgress />
                                ))}
                        </>
                    )}
                    {!showConfirmFirst && (
                        <>
                            {confirmText &&
                                (!btnConfirmLoader ? (
                                    <AppButton
                                        role="button"
                                        buttonVariant={
                                            showButtonBackgroundColor
                                                ? "primary-filled"
                                                : "grey-text"
                                        }
                                        onClick={onConfirm}
                                    >
                                        {confirmText}
                                    </AppButton>
                                ) : (
                                    <AppCircularProgress />
                                ))}
                            {showCancelButton && (
                                <AppButton
                                    buttonVariant={
                                        showButtonBackgroundColor
                                            ? "primary-filled"
                                            : "grey-text"
                                    }
                                    role="button"
                                    onClick={onClose}
                                >
                                    {cancelButtonText}
                                </AppButton>
                            )}
                        </>
                    )}
                </DialogActions>
            </StyledDialogWrapper>
        </Dialog>
    );
};
